import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';
import MockupPreview, { MockupType } from '../../components/marketing/MockupPreview';
import ScreenshotFrame from '../../components/marketing/ScreenshotFrame';
import { featureHighlights, featureCategories } from '../../data/marketing/features';

interface PreviewConfig {
  mockupType: MockupType;
  screenshot?: string; // filename under /screenshots/marketing (no extension)
  url: string; // fake URL shown in screenshot chrome
  alt: string;
}

/**
 * Maps a feature title to the minimum paid tier required for the listed
 * details. The Seedling (free) tier includes text observations, assessments,
 * CAPS tracking, and the parent portal — media uploads, PDF reports, and
 * advanced analytics are gated behind the Sprout paid tier.
 */
const paidTierByTitle: Record<string, string> = {
  'Observation Tracking': 'Sprout plan',
  'Reports & Analytics': 'Sprout plan',
};

/**
 * Maps each highlighted feature to a real app screenshot (preferred) plus a
 * MockupPreview fallback. If the screenshot is missing or fails to load, the
 * MockupPreview renders instead so the page never shows a broken image.
 *
 * The Parent Portal intentionally has no screenshot: we'd need to log in as a
 * guardian to capture one, and the MockupPreview shows a composite view that
 * avoids exposing any individual family's data.
 */
const previewByTitle: Record<string, PreviewConfig> = {
  'Observation Tracking': {
    mockupType: 'observations',
    screenshot: 'observations',
    url: 'app.sprout.education/observations',
    alt: 'Sprout observations list — recent narrative observations with CAPS learning areas',
  },
  'CAPS-Aligned Assessments': {
    mockupType: 'assessments',
    screenshot: 'assessments',
    url: 'app.sprout.education/assessments',
    alt: 'Sprout assessments — CAPS-aligned scored evaluations',
  },
  'Parent Portal': {
    mockupType: 'guardian',
    url: 'app.sprout.education/guardian',
    alt: 'Sprout parent portal preview',
  },
  'Reports & Analytics': {
    mockupType: 'reports',
    screenshot: 'reports',
    url: 'app.sprout.education/reports',
    alt: 'Sprout reports — generated child progress reports with PDF export',
  },
};
import {
  EyeIcon,
  ClipboardDocumentCheckIcon,
  UserGroupIcon,
  ChartBarIcon,
  BuildingOfficeIcon,
  UsersIcon,
  ShieldCheckIcon,
  CameraIcon,
  AcademicCapIcon,
  PresentationChartLineIcon,
  BellIcon,
  LockClosedIcon,
  ArrowTrendingUpIcon,
} from '@heroicons/react/24/outline';

const iconMap: Record<string, React.FC<{ className?: string }>> = {
  eye: EyeIcon,
  'clipboard-document-check': ClipboardDocumentCheckIcon,
  'user-group': UserGroupIcon,
  'chart-bar': ChartBarIcon,
  'building-office': BuildingOfficeIcon,
  users: UsersIcon,
  'shield-check': ShieldCheckIcon,
  camera: CameraIcon,
  'academic-cap': AcademicCapIcon,
  'presentation-chart-line': PresentationChartLineIcon,
  bell: BellIcon,
  'lock-closed': LockClosedIcon,
  'arrow-trending-up': ArrowTrendingUpIcon,
};

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-50px' },
  transition: { duration: 0.5 },
};

const FeaturesPage: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Features — Sprout Educational Platform</title>
        <meta name="description" content="Observations, assessments, CAPS curriculum mapping, parent portal, reports, and more. Everything your early childhood centre needs." />
        <meta property="og:title" content="Features — Sprout Educational Platform" />
        <link rel="canonical" href="https://sprout-ui-pi.vercel.app/features" />
      </Helmet>

      {/* Header */}
      <section className="pt-28 pb-16 bg-gradient-to-br from-primary-50 via-white to-secondary-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl sm:text-5xl font-display font-bold text-gray-900 dark:text-white"
          >
            Features
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mt-4 text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto"
          >
            A complete observation and assessment platform designed around the South African CAPS curriculum.
          </motion.p>
        </div>
      </section>

      {/* Core Features */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-20">
            {featureHighlights.map((feature, i) => {
              const Icon = iconMap[feature.icon];
              const isReversed = i % 2 === 1;

              return (
                <motion.div
                  key={feature.title}
                  {...fadeInUp}
                  className={`flex flex-col lg:flex-row items-center gap-12 ${
                    isReversed ? 'lg:flex-row-reverse' : ''
                  }`}
                >
                  {/* Content */}
                  <div className="flex-1">
                    <div className="w-14 h-14 bg-primary-100 rounded-xl flex items-center justify-center mb-6">
                      {Icon && <Icon className="w-7 h-7 text-primary-600" />}
                    </div>
                    <div className="flex items-center gap-3 flex-wrap">
                      <h2 className="text-2xl sm:text-3xl font-display font-bold text-gray-900 dark:text-white">
                        {feature.title}
                      </h2>
                      {paidTierByTitle[feature.title] && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300">
                          {paidTierByTitle[feature.title]}
                        </span>
                      )}
                    </div>
                    <p className="mt-4 text-gray-600 dark:text-gray-300 leading-relaxed">{feature.description}</p>
                    <ul className="mt-6 space-y-3">
                      {feature.details.map(detail => (
                        <li key={detail} className="flex items-start">
                          <svg className="w-5 h-5 text-secondary-500 mr-3 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          <span className="text-gray-600 dark:text-gray-300">{detail}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Real screenshot (with MockupPreview fallback) */}
                  <div className="flex-1 w-full">
                    {(() => {
                      const preview = previewByTitle[feature.title] ?? {
                        mockupType: 'dashboard' as MockupType,
                        url: 'app.sprout.education/dashboard',
                        alt: feature.title,
                      };
                      const fallback = <MockupPreview type={preview.mockupType} />;
                      return preview.screenshot ? (
                        <ScreenshotFrame
                          src={`/screenshots/marketing/${preview.screenshot}.png`}
                          alt={preview.alt}
                          url={preview.url}
                          fallback={fallback}
                        />
                      ) : (
                        fallback
                      );
                    })()}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* By Audience */}
      <section className="py-20 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {featureCategories.map((category, ci) => (
            <motion.div key={category.name} {...fadeInUp} className={ci > 0 ? 'mt-20' : ''}>
              <div className="text-center mb-12">
                <h2 className="text-2xl sm:text-3xl font-display font-bold text-gray-900 dark:text-white">
                  {category.name}
                </h2>
                <p className="mt-2 text-gray-600 dark:text-gray-300">{category.tagline}</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {category.features.map(feature => {
                  const Icon = iconMap[feature.icon];
                  return (
                    <Card key={feature.title} className="text-center">
                      <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                        {Icon && <Icon className="w-6 h-6 text-primary-600" />}
                      </div>
                      <h3 className="font-display font-semibold text-gray-900 dark:text-white">{feature.title}</h3>
                      <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">{feature.description}</p>
                    </Card>
                  );
                })}
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-primary-600">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-display font-bold text-white">Ready to get started?</h2>
          <p className="mt-4 text-primary-100">
            Start free with unlimited children. Upgrade anytime to unlock photos, PDF reports, and analytics.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/pricing">
              <Button variant="secondary" size="lg" className="bg-white text-primary-700 hover:bg-gray-50">
                View Pricing
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="ghost" size="lg" className="text-white border-white/30 border hover:bg-white/10">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default FeaturesPage;
